import { useState, useCallback } from 'react';
import useSWR, { mutate } from 'swr';

import { servicesAPI } from '../services_api';

export function useCustomer(id) {
  const [dirty, setDirty] = useState(false);
  const [remoteData, setRemoteData] = useState(null);

  const key = `customer/${id}/raw`;
  const { data, error } = useSWR(
    key,
    async () => {
      if (id === undefined || id === null || id === 0) {
        return {};
      }

      const remote = await servicesAPI.CustomerServices('getById', id);
      setRemoteData(remote);

      return remote;
    },
    { refreshInterval: 0, revalidateOnFocus: false }
  );

  const saveCustomer = useCallback(async () => {
    if (!dirty || !id) {
      // Nothing to save.
      return;
    }

    servicesAPI.CustomerServices('update', data, id);
    setDirty(false);
  }, [data, dirty, id]);

  const setCustomer = newData => {
    mutate(key, { ...data, ...newData }, false);
    setDirty(true);
  };

  return {
    customer: data,
    dirty,
    error,
    setCustomer,
    saveCustomer,
    resetCustomerFields: fields => {
      const originalData = remoteData;
      const dataToReset = fields.reduce((memo, field) => {
        return {
          ...memo,
          [field]: originalData[field]
        };
      }, {});

      setCustomer({
        ...data,
        ...dataToReset
      });
    }
  };
}

export function useCustomerPreferences(id) {
  const [dirty, setDirty] = useState(false);
  const [remoteData, setRemoteData] = useState(null);

  const key = `customer/${id}`;
  const { data, error } = useSWR(
    key,
    async () => {
      if (id === undefined || id === null || id === 0) {
        return {};
      }
      const remote = await servicesAPI.CustomerServices('getPreferences', id);
      setRemoteData(remote);

      return remote;
    },
    { refreshInterval: 0, revalidateOnFocus: false }
  );

  const saveCustomerPreferences = useCallback(async () => {
    if (!dirty || !id) {
      // Nothing to save.
      return;
    }

    servicesAPI.CustomerServices('updatePreferences', data, id);
    setDirty(false);
  }, [data, dirty, id]);

  const setCustomerPreferences = newData => {
    mutate(key, { ...data, ...newData }, false);
    setDirty(true);
  };

  return {
    customerPreferences: data,
    dirty,
    error,
    setCustomerPreferences,
    saveCustomerPreferences,
    resetCustomerPreferencesFields: fields => {
      const originalData = remoteData;
      const dataToReset = fields.reduce((memo, field) => {
        return {
          ...memo,
          [field]: originalData[field]
        };
      }, {});

      setCustomerPreferences({
        ...data,
        ...dataToReset
      });
    }
  };
}

export function useCustomerBillingAccount(id) {
  const { data = null } = useSWR(id ? `account/customer/${id}` : null, () =>
    servicesAPI.BillingServices('getAccountByCustomerId', id)
  );

  return data;
}

export function useCustomerBills(id) {
  const { data = [] } = useSWR(id ? `account/transactions/${id}` : null, () =>
    servicesAPI.BillingServices('getBillsByAccountId', id)
  );

  return data;
}

export function useCustomerMonthlyBills(id) {
  const { data = null } = useSWR(
    id ? `account/transactions/${id}/monthly` : null,
    () => servicesAPI.BillingServices('getMonthlyBillsByAccountId', id)
  );

  return data;
}

export function useTariff(id) {
  const { data = null } = useSWR(id ? `tariff/${id}` : null, () =>
    servicesAPI.BillingServices('getTariff', id)
  );

  return data;
}

export function useLanceToCustomers(customerId) {
  const { data = [] } = useSWR(
    customerId ? `customer_to_lances/customer/${customerId}` : null,
    () =>
      servicesAPI.CustomerServices('getLanceCustomerIdsByCustomer', customerId)
  );

  return data;
}

export function useLance(lanceId) {
  const { data = null } = useSWR(lanceId ? `lance/${lanceId}` : null, () =>
    servicesAPI.InventoryServices('getLance', lanceId)
  );

  return data;
}

export function useCustomerLances(customerId) {
  const { data = [] } = useSWR(
    customerId ? `lance/customer/${customerId}` : [],
    () => servicesAPI.InventoryServices('getLancesByCustomerId', customerId)
  );

  return data;
}

export function useChargingSession(sessionId) {
  const { data = null } = useSWR(
    sessionId ? `session/${sessionId}` : null,
    () => servicesAPI.MeteringServices('getSession', sessionId)
  );

  return data;
}

export function useChargingSessionByLanceToCustomer(lanceToCustomerId) {
  const { data = null } = useSWR(
    lanceToCustomerId ? `session/lance/${lanceToCustomerId}` : null,
    () =>
      servicesAPI.MeteringServices(
        'getSessionByLanceToCustomer',
        lanceToCustomerId
      )
  );

  return data;
}

export function usePhalanx(phalanxId) {
  const { data = null } = useSWR(
    phalanxId ? `phalanx/${phalanxId}` : null,
    () => servicesAPI.InventoryServices('getPhalanx', phalanxId)
  );

  return data;
}

export function usePhalanxByReference(phalanxRef) {
  const { data = null } = useSWR(
    phalanxRef ? `phalanx/${phalanxRef}` : null,
    () => servicesAPI.InventoryServices('getPhalanxByReference', phalanxRef)
  );

  return data;
}

export function useMeter(meterId) {
  const { data = null } = useSWR(meterId ? `meter/${meterId}` : null, () =>
    servicesAPI.MeteringServices('getMeter', meterId)
  );

  return data;
}

export function useCustomerVehicles(customerId) {
  const [dirty, setDirty] = useState(false);
  const [remoteData, setRemoteData] = useState(null);

  const key = `vehicles/customer/${customerId}`;
  const { data, error } = useSWR(
    key,
    async () => {
      if (customerId === undefined || customerId === null || customerId === 0) {
        return [];
      }
      const remote = await servicesAPI.CustomerServices(
        'getCustomerVehicles',
        customerId
      );
      setRemoteData(remote);

      return remote;
    },
    { refreshInterval: 0, revalidateOnFocus: false }
  );

  const saveVehicles = useCallback(async () => {
    if (!dirty || !customerId) {
      // Nothing to save.
      return;
    }

    servicesAPI.CustomerServices('updateVehicles', data, customerId);
    setDirty(false);
  }, [data, dirty, customerId]);

  const setVehicles = newData => {
    mutate(key, { ...data, ...newData }, false);
    setDirty(true);
  };

  return {
    customerVehicles: data,
    dirty,
    error,
    setVehicles,
    saveVehicles,
    resetVehiclesFields: fields => {
      const originalData = remoteData;
      const dataToReset = fields.reduce((memo, field) => {
        return {
          ...memo,
          [field]: originalData[field]
        };
      }, {});

      setVehicles({
        ...data,
        ...dataToReset
      });
    }
  };
}

export function useReferenceVehicles() {
  const { data = [] } = useSWR(`vehicles/reference/`, () =>
    servicesAPI.CustomerServices('getReferenceVehicles')
  );

  return data;
}

export function useVehicleData(vehicleId) {
  const { data = null } = useSWR(
    vehicleId ? `vehicle/${vehicleId}` : null,
    () => servicesAPI.CustomerServices('getVehicleData', vehicleId)
  );

  return data;
}

export function useSessions(lanceToCustomerId) {
  const key = `sessions/lance_customer/${lanceToCustomerId}`;
  const { data = [] } = useSWR(
    key,
    async () => {
      if (lanceToCustomerId === undefined || lanceToCustomerId === null) {
        return [];
      }
      const remote = await servicesAPI.MeteringServices(
        'getSessionsByLanceToCustomer',
        lanceToCustomerId
      );

      return remote;
    },
    { refreshInterval: 0, revalidateOnFocus: false }
  );

  return {
    sessions: data
  };
}

export function useSession(sessionId) {
  const { data = null } = useSWR(`/session/${sessionId}`, () =>
    servicesAPI.MeteringServices('getSession', sessionId)
  );

  return data;
}

export function useExternalAccounts(customerId) {
  const [dirty, setDirty] = useState(false);
  const [remoteData, setRemoteData] = useState(null);

  const key = `external_accounts/customer/${customerId}`;
  const { data, error } = useSWR(
    key,
    async () => {
      if (customerId === undefined || customerId === null) {
        return null;
      }
      const remote = await servicesAPI.CustomerServices(
        'getExternalAccounts',
        customerId
      );
      setRemoteData(remote);

      return remote;
    },
    { refreshInterval: 0, revalidateOnFocus: false }
  );

  const saveExternalAccounts = useCallback(async () => {
    if (!dirty || !customerId) {
      // Nothing to save.
      return;
    }

    servicesAPI.CustomerServices('updateExternalAccounts', data, customerId);
    setDirty(false);
  }, [data, dirty, customerId]);

  const setExternalAccounts = newData => {
    mutate(key, { ...data, ...newData }, false);
    setDirty(true);
  };

  return {
    externalAccounts: data,
    dirty,
    error,
    setExternalAccounts,
    saveExternalAccounts,
    resetExternalAccountsFields: fields => {
      const originalData = remoteData;
      const dataToReset = fields.reduce((memo, field) => {
        return {
          ...memo,
          [field]: originalData[field]
        };
      }, {});

      setExternalAccounts({
        ...data,
        ...dataToReset
      });
    }
  };
}
